import {
  Building2,
  Circle,
  FileCheck2,
  History,
  LogIn,
  Send,
  ShieldAlert,
  UserRound
} from 'lucide-react'
import { cn } from '@/shared/lib/utils'
import { Empty, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from '@/shared/ui/empty'
import {
  ACTIVITY_FAMILY,
  type ActivityEvent,
  type ActivityFamily,
  activityActorLabel,
  activityDetailLine,
  activityFamilyOf,
  activityTime,
  groupEventsByDay
} from '../models/client-account-activity'
import { clientAccountEventLabel } from '../models/client-account-admin-v2'

/**
 * La bitácora de la cuenta, agrupada por día y del movimiento más nuevo al más viejo.
 *
 * Cada renglón dice QUÉ pasó (con un ícono por familia), un detalle tenue sacado de `details`
 * y QUIÉN lo hizo: el cliente, una persona del equipo o el sistema. «Sistema» no es un hueco
 * —son los crons de vencimiento e inactividad, sin persona detrás— y un evento del cliente
 * jamás se rotula así.
 */
type ClientAccountEventsListProps = {
  events: ActivityEvent[]
}

const FAMILY_ICONS: Record<ActivityFamily, typeof Circle> = {
  [ACTIVITY_FAMILY.account]: UserRound,
  [ACTIVITY_FAMILY.invitation]: Send,
  [ACTIVITY_FAMILY.signIn]: LogIn,
  [ACTIVITY_FAMILY.legal]: FileCheck2,
  [ACTIVITY_FAMILY.security]: ShieldAlert,
  [ACTIVITY_FAMILY.access]: Building2,
  [ACTIVITY_FAMILY.other]: Circle
}

export function ClientAccountEventsList({ events }: ClientAccountEventsListProps) {
  const groups = groupEventsByDay(events)
  // Se decide por lo que se DIBUJA (los grupos), no por la entrada: así el vacío y la lista
  // no pueden discrepar.
  if (groups.length === 0) {
    return (
      <Empty className="py-6">
        <EmptyHeader>
          <EmptyMedia>
            <History className="h-6 w-6 text-muted-foreground" aria-hidden />
          </EmptyMedia>
          <EmptyTitle>Sin movimientos registrados</EmptyTitle>
          <EmptyDescription>
            Aquí queda lo que pasa con la cuenta: quién la abrió, qué aceptó el cliente, quién
            concedió o quitó inmuebles y quién la bloqueó.
          </EmptyDescription>
        </EmptyHeader>
      </Empty>
    )
  }

  // No hay un id por evento: la clave sale de acción + instante, y si dos coinciden exactos se
  // les suma un contador. Así no se usa el índice (que cambia al llegar páginas más viejas).
  const rowKeys = Object.fromEntries(
    groups.map((group) => {
      const seen = new Map<string, number>()
      return [
        group.key,
        group.events.map((event) => {
          const base = `${event.action}-${event.at}`
          const count = seen.get(base) ?? 0
          seen.set(base, count + 1)
          return `${base}-${count}`
        })
      ]
    })
  )

  return (
    <div className="flex flex-col gap-4">
      {groups.map((group) => (
        <section key={group.key} aria-label={group.heading} className="flex flex-col gap-2">
          <h4 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            {group.heading}
          </h4>
          <ol className="flex flex-col">
            {group.events.map((event, index) => {
              const rowKey = rowKeys[group.key][index]
              const Icon = FAMILY_ICONS[activityFamilyOf(event.action)]
              const detail = activityDetailLine(event.action, event.details)
              const isLast = index === group.events.length - 1
              return (
                <li key={rowKey} className="relative flex gap-2.5 pb-3 last:pb-0">
                  {/* El hilo NO se dibuja bajo el último ícono del día, o el grupo parece cortado. */}
                  <div className="flex flex-col items-center">
                    <span
                      className={cn(
                        'flex h-6 w-6 shrink-0 items-center justify-center rounded-full',
                        event.actorKind === 'client'
                          ? 'bg-primary/10 text-primary'
                          : 'bg-secondary text-muted-foreground dark:bg-accent'
                      )}
                    >
                      <Icon className="h-3.5 w-3.5" aria-hidden />
                    </span>
                    {isLast ? null : <span className="mt-1 w-px flex-1 bg-border" />}
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="text-xs font-medium leading-tight">
                      {clientAccountEventLabel(event.action)}
                    </span>
                    {detail ? (
                      <span className="break-words text-xs text-muted-foreground">{detail}</span>
                    ) : null}
                    <span className="text-[11px] text-muted-foreground/80">
                      {[activityActorLabel(event), activityTime(event.at)]
                        .filter(Boolean)
                        .join(' · ')}
                    </span>
                  </div>
                </li>
              )
            })}
          </ol>
        </section>
      ))}
    </div>
  )
}
